import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Eye, Users, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function TransparencySettings({ tripId }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    show_participants_in_itinerary: true,
    show_payment_status: true
  });

  useEffect(() => {
    fetchSettings();
  }, [tripId]);

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/transparency`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching transparency settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const response = await api.put(`/trips/${tripId}/transparency`, {
        [key]: value
      });
      setSettings(response.data);
      toast.success('Configuração atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-orange-500" />
          Transparência
        </CardTitle>
        <CardDescription>
          Configure o que os membros podem ver sobre outros participantes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Participants in Itinerary */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <Label htmlFor="show-participants" className="font-medium">
                Participantes no Roteiro
              </Label>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Permitir que membros vejam quem vai participar de cada atividade
            </p>
          </div>
          <Switch
            id="show-participants"
            checked={settings.show_participants_in_itinerary}
            onCheckedChange={(checked) => updateSetting('show_participants_in_itinerary', checked)}
            disabled={saving}
          />
        </div>

        {/* Payment Status */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <Label htmlFor="show-payments" className="font-medium">
                Status de Pagamentos
              </Label>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Permitir que membros vejam quem pagou e quem não pagou
            </p>
          </div>
          <Switch
            id="show-payments"
            checked={settings.show_payment_status}
            onCheckedChange={(checked) => updateSetting('show_payment_status', checked)}
            disabled={saving}
          />
        </div>

        {/* Info note */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <strong>Nota:</strong> Você ainda pode configurar a visibilidade individualmente para cada item do roteiro ou pagamento, sobrescrevendo estas configurações gerais.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default TransparencySettings;
